import { fmtPt, pathLength, polygonArea, polygonCentroid } from '../measure'
import type { Point2 } from './viewTypes'

// ---------------------------------------------------------------------------
// Cotas do desenho (medições persistentes).
//
// Uma medição feita na superfície 2D deixou de ser efémera: ao terminar,
// fica DESENHADA como cota, viaja com a vista gravada e sai nos
// exports. É o que o 3D já fazia, e é o que um desenho técnico quer.
//
// Uma cota é a polilinha que o utilizador clicou (metros no plano da
// vista, as mesmas coordenadas das anotações) mais a maneira de a ler:
//   - 'linear': dois pontos, uma distância;
//   - 'chain':  três ou mais pontos abertos, um valor por troço e o
//     total (opcional) no fim;
//   - 'area':   polígono fechado, área e perímetro no centróide.
//
// A COTA DO TEXTO declara-se em MILÍMETROS DE PAPEL, como nas anotações
// (2,5 mm é a altura corrente de uma legenda ISO 3098). O mesmo vale
// para os traços de extensão e para o afastamento do texto: são
// grandezas de PAPEL, não de modelo, e cada renderer converte para a
// sua unidade.
//
// A geometria sai daqui numa forma neutra (`DimGeometry`): pontos em
// metros do desenho, direcções unitárias e afastamentos em mm de papel.
// Quem desenha (SVG, PDF, DXF, pré-visualização) aplica os
// afastamentos na SUA unidade, e assim os quatro dizem exactamente a
// mesma coisa sem repetir trigonometria.
//
// NÃO se usa a entidade DIMENSION do DXF: em R12 é um bloco anónimo com
// uma dúzia de campos que cada CAD regenera à sua maneira (e com o
// estilo de cota do ficheiro de destino, que não é o nosso). Linhas e
// texto é o que os writers de referência fazem e é o que se lê igual em
// todo o lado.
//
// Puro: só números e strings, testável em Node. (Importa de `../measure`
// as funções que já lá estavam - shoelace, centróide, comprimento e
// formatação PT-PT - em vez de as repetir. O `measure.ts` só importa do
// `lib` um TIPO, que desaparece na compilação: não há ciclo em
// execução.)
// ---------------------------------------------------------------------------

export type Dimension2DKind = 'linear' | 'chain' | 'area'

export interface Dimension2D {
  id: string
  kind: Dimension2DKind
  /** Vértices, em coordenadas do desenho (metros no plano da vista).
   *  Numa 'area' o último ponto NÃO repete o primeiro. */
  points: Point2[]
  /** Altura do texto em milímetros de PAPEL. Ausente = DIM_SIZE_MM. */
  sizeMm?: number
  /** Encadeada: escrever também o comprimento total. Ausente = sim. */
  total?: boolean
  /** Anéis ADICIONAIS de uma cota de área: os furos da secção (e os
   *  contornos separados do mesmo elemento). Desenham-se tracejados como
   *  o contorno principal, e é isso que faz um furo parecer um furo no
   *  ecrã, no SVG, no PDF e no DXF. */
  rings?: Point2[][]
  /** Área EXACTA, em m². Escreve-se quando a medição veio de geometria
   *  com arcos (um pilar redondo mede π·r², não o polígono inscrito) ou
   *  de uma região com furos. Ausente = calcula-se dos pontos, que é o
   *  que sempre se fez e o que as cotas clicadas à mão continuam a
   *  fazer. */
  area?: number
  /** Perímetro EXACTO, em metros (mesma razão da `area`: um arco mede
   *  r·|Δ|, não a corda). */
  perimeter?: number
}

/** Altura por omissão do texto das cotas, em mm de papel. A mesma das
 *  anotações: as duas coisas convivem no mesmo desenho. */
export const DIM_SIZE_MM = 2.5

/** Camada própria das cotas (DXF e agrupamento no SVG). Maiúsculas e sem
 *  acentos: seguro como nome de layer R12. */
export const DIM_LAYER = 'COTAS'

/** Cor por omissão do traço e do texto das cotas. */
export const DIM_COLOR = '#111111'

/** Espessura do traço da cota, em mm de papel: mais leve do que o corte
 *  (0,35) e um pouco mais firme do que a projecção (0,13), que é como
 *  uma cota se lê num desenho. */
export const DIM_WIDTH_MM = 0.18

/** MEIO comprimento do traço de extensão nas pontas, em mm de papel (o
 *  traço é centrado no vértice, portanto mede o dobro disto). */
export const DIM_TICK_MM = 1.2

/** Afastamento do texto acima da linha de cota, em mm de papel. */
export const DIM_TEXT_GAP_MM = 0.9

/** Afastamento do total de uma cota encadeada, em mm de papel. Bem mais
 *  do que o dos troços, para o total não se confundir com eles. */
export const DIM_TOTAL_GAP_MM = 5

/** Tracejado do contorno das cotas de área: traço e intervalo em mm de
 *  papel. */
export const DIM_DASH_MM: [number, number] = [1.8, 1.2]

/** Máximo de vértices de uma cota. Um desenho não é um ficheiro de
 *  pontos: sem limite, um clique preso enchia o localStorage. */
export const DIM_MAX_POINTS = 200

/** Comprimento abaixo do qual um troço não recebe texto nem traços de
 *  extensão (dois cliques no mesmo sítio), em metros. */
const MIN_SEG = 1e-9

export function newDimensionId(now = Date.now(), rand = Math.random()): string {
  return `d2d_${now.toString(36)}_${Math.floor(rand * 1e6).toString(36)}`
}

/** Pontos finitos, sem repetidos consecutivos (dois cliques no mesmo
 *  sítio não fazem troço nenhum) e truncados no máximo. */
export function cleanDimensionPoints(raw: unknown): Point2[] {
  if (!Array.isArray(raw)) return []
  const out: Point2[] = []
  for (const p of raw) {
    if (!Array.isArray(p) || p.length < 2) continue
    const x = Number(p[0])
    const y = Number(p[1])
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue
    const last = out[out.length - 1]
    if (last && Math.hypot(x - last[0], y - last[1]) < MIN_SEG) continue
    out.push([x, y])
    if (out.length >= DIM_MAX_POINTS) break
  }
  return out
}

/** Cria uma cota. O `kind` pedido é CORRIGIDO pelo número de pontos:
 *  dois pontos são sempre uma distância e uma área precisa de três. */
export function makeDimension(
  points: Point2[],
  kind: Dimension2DKind,
  id: string,
  opts?: {
    sizeMm?: number
    total?: boolean
    rings?: Point2[][]
    area?: number
    perimeter?: number
  },
): Dimension2D | null {
  const pts = cleanDimensionPoints(points)
  if (pts.length < 2) return null
  const real: Dimension2DKind =
    pts.length === 2 ? 'linear' : kind === 'area' ? 'area' : 'chain'
  const out: Dimension2D = { id, kind: real, points: pts }
  const size = opts?.sizeMm
  if (Number.isFinite(size) && (size as number) > 0) out.sizeMm = size
  if (opts?.total === false) out.total = false
  // Anéis e valores exactos só fazem sentido numa cota de área.
  if (real === 'area') {
    const rings = cleanDimensionRings(opts?.rings)
    if (rings.length > 0) out.rings = rings
    if (Number.isFinite(opts?.area) && (opts?.area as number) >= 0) {
      out.area = opts?.area
    }
    if (Number.isFinite(opts?.perimeter) && (opts?.perimeter as number) >= 0) {
      out.perimeter = opts?.perimeter
    }
  }
  return out
}

/** Máximo de anéis adicionais numa cota de área. Uma secção com dezenas
 *  de furos desenha-se na mesma; o tecto é só para o `localStorage` não
 *  levar com geometria patológica. */
export const DIM_MAX_RINGS = 32

/** Anéis limpos: cada um com pelo menos três pontos. */
export function cleanDimensionRings(raw: unknown): Point2[][] {
  if (!Array.isArray(raw)) return []
  const out: Point2[][] = []
  for (const ring of raw) {
    const pts = cleanDimensionPoints(ring)
    if (pts.length < 3) continue
    out.push(pts)
    if (out.length >= DIM_MAX_RINGS) break
  }
  return out
}

export function addDimension(
  list: Dimension2D[],
  d: Dimension2D,
): Dimension2D[] {
  return [...list.filter((v) => v.id !== d.id), d]
}

export function removeDimension(list: Dimension2D[], id: string): Dimension2D[] {
  return list.filter((v) => v.id !== id)
}

/** Altura do texto em mm de papel (com o valor por omissão aplicado). */
export function dimensionSizeMm(d: Dimension2D): number {
  return Number.isFinite(d.sizeMm) && (d.sizeMm as number) > 0
    ? (d.sizeMm as number)
    : DIM_SIZE_MM
}

/** Converte uma grandeza de PAPEL (mm) em unidades do desenho (metros)
 *  para uma dada escala: 2,5 mm a 1:100 = 0,25 m. */
export function dimMmToDrawing(mm: number, scaleDenom: number): number {
  const denom = Number.isFinite(scaleDenom) && scaleDenom > 0 ? scaleDenom : 100
  return (mm / 1000) * denom
}

/** Altura do texto em unidades do desenho (metros) para uma escala. */
export function dimensionHeightM(
  sizeMm: number | undefined,
  scaleDenom: number,
): number {
  const mm =
    Number.isFinite(sizeMm) && (sizeMm as number) > 0
      ? (sizeMm as number)
      : DIM_SIZE_MM
  return dimMmToDrawing(mm, scaleDenom)
}

/** Comprimento total (perímetro, nas de área). O valor EXACTO manda
 *  quando lá está: uma cota que nasceu de um círculo mede 2·π·r, e os
 *  pontos que ela leva são só a amostragem com que se desenha. */
export function dimensionLength(d: Dimension2D): number {
  if (Number.isFinite(d.perimeter)) return d.perimeter as number
  let total = pathLength(d.points, d.kind === 'area')
  for (const ring of d.rings ?? []) total += pathLength(ring, true)
  return total
}

/** Área; 0 fora das cotas de área. Os anéis adicionais subtraem
 *  (par-ímpar de um nível: são os furos da secção). */
export function dimensionArea(d: Dimension2D): number {
  if (d.kind !== 'area') return 0
  if (Number.isFinite(d.area)) return d.area as number
  let a = polygonArea(d.points)
  for (const ring of d.rings ?? []) a -= polygonArea(ring)
  return Math.max(a, 0)
}

/** Etiqueta curta, para listas e mensagens. */
export function dimensionLabel(d: Dimension2D): string {
  if (d.kind === 'area') return `A = ${fmtPt(dimensionArea(d))} m²`
  return `${fmtPt(dimensionLength(d))} m`
}

/** Linha de estado: o que se mostra na barra sobre uma cota. */
export function dimensionSummary(d: Dimension2D): string {
  if (d.kind === 'area') {
    return `A = ${fmtPt(dimensionArea(d))} m² · perímetro ${fmtPt(
      dimensionLength(d),
    )} m`
  }
  if (d.kind === 'linear') return `Distância ${fmtPt(dimensionLength(d))} m`
  return `Comprimento ${fmtPt(dimensionLength(d))} m`
}

// --- Geometria para os renderers -------------------------------------------

/** Segmento de traço, em coordenadas do desenho (metros). */
export interface DimLine {
  a: Point2
  b: Point2
}

/** Traço de extensão: perpendicular à linha de cota e CENTRADO no
 *  vértice. `n` é unitário, em coordenadas do desenho. */
export interface DimTick {
  at: Point2
  nx: number
  ny: number
}

/** Texto de uma cota. A âncora é a LINHA DE BASE, centrada
 *  horizontalmente, como nos writers das anotações. */
export interface DimText {
  /** Ponto sobre a linha de cota, em metros do desenho. */
  at: Point2
  text: string
  /** Rotação, em radianos, no referencial do DESENHO (y para cima,
   *  sentido directo). Normalizada para (-pi/2, pi/2]: o texto nunca
   *  sai de pernas para o ar. */
  angleRad: number
  /** Normal unitária: a direcção em que o texto se afasta do traço. */
  nx: number
  ny: number
  /** Afastamento ao longo da normal, em mm de PAPEL. Pode ser negativo
   *  (segunda linha de uma etiqueta). */
  offsetMm: number
}

/** Tudo o que é preciso para desenhar uma cota, em unidades neutras. */
export interface DimGeometry {
  /** Linhas de cota (traço contínuo). */
  lines: DimLine[]
  /** Traços de extensão das pontas. */
  ticks: DimTick[]
  /** Contorno tracejado (só nas cotas de área). */
  dashed: DimLine[]
  texts: DimText[]
}

/** Direcção unitária e ângulo normalizado de um troço, mais a normal do
 *  lado onde o texto se escreve. Devolve null se o troço for nulo. */
function segmentFrame(
  a: Point2,
  b: Point2,
): { ux: number; uy: number; nx: number; ny: number; angle: number; len: number } | null {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const len = Math.hypot(dx, dy)
  if (!(len > MIN_SEG)) return null
  let ux = dx / len
  let uy = dy / len
  let angle = Math.atan2(uy, ux)
  // Texto sempre a ler da esquerda para a direita: um troço que aponte
  // para trás escreve-se ao contrário, com a normal a acompanhar (o
  // texto fica sempre do mesmo lado do traço).
  if (angle > Math.PI / 2 + 1e-12) {
    angle -= Math.PI
    ux = -ux
    uy = -uy
  } else if (angle <= -Math.PI / 2 + 1e-12) {
    angle += Math.PI
    ux = -ux
    uy = -uy
  }
  return { ux, uy, nx: -uy, ny: ux, angle, len }
}

/** Geometria completa de uma cota. Pura e determinística. */
export function dimensionGeometry(d: Dimension2D): DimGeometry {
  const out: DimGeometry = { lines: [], ticks: [], dashed: [], texts: [] }
  const pts = d.points
  if (!pts || pts.length < 2) return out

  if (d.kind === 'area') {
    if (pts.length < 3) return out
    for (let i = 0; i < pts.length; i++) {
      out.dashed.push({ a: pts[i], b: pts[(i + 1) % pts.length] })
    }
    // Furos (e outros contornos do mesmo elemento): o mesmo tracejado.
    // Sem eles, uma secção com um vazio media a área certa e desenhava
    // uma mancha cheia, que é uma cota a dizer o contrário do que mostra.
    for (const ring of d.rings ?? []) {
      for (let i = 0; i < ring.length; i++) {
        out.dashed.push({ a: ring[i], b: ring[(i + 1) % ring.length] })
      }
    }
    const c = polygonCentroid(pts)
    const size = dimensionSizeMm(d)
    // Duas linhas de texto no centróide: a área por cima, o perímetro
    // por baixo. O afastamento da segunda desce uma linha e meia da
    // altura do texto, para as duas não se tocarem.
    out.texts.push({
      at: c,
      text: `A = ${fmtPt(dimensionArea(d))} m²`,
      angleRad: 0,
      nx: 0,
      ny: 1,
      offsetMm: DIM_TEXT_GAP_MM,
    })
    out.texts.push({
      at: c,
      text: `P = ${fmtPt(dimensionLength(d))} m`,
      angleRad: 0,
      nx: 0,
      ny: 1,
      offsetMm: DIM_TEXT_GAP_MM - size * 1.5,
    })
    return out
  }

  // Linear e encadeada: um traço por troço, extensões nas pontas de cada
  // troço e o valor centrado sobre a linha.
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]
    const b = pts[i + 1]
    const f = segmentFrame(a, b)
    if (!f) continue
    out.lines.push({ a, b })
    // O traço de extensão do vértice partilhado só se escreve uma vez
    // (o `b` de um troço é o `a` do seguinte).
    if (i === 0) out.ticks.push({ at: a, nx: f.nx, ny: f.ny })
    out.ticks.push({ at: b, nx: f.nx, ny: f.ny })
    out.texts.push({
      at: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
      text: `${fmtPt(f.len)} m`,
      angleRad: f.angle,
      nx: f.nx,
      ny: f.ny,
      offsetMm: DIM_TEXT_GAP_MM,
    })
  }

  // Total da encadeada: sobre a corda do primeiro ao último ponto, bem
  // afastado dos valores dos troços.
  if (d.kind === 'chain' && pts.length > 2 && d.total !== false) {
    const a = pts[0]
    const b = pts[pts.length - 1]
    const f = segmentFrame(a, b)
    if (f) {
      out.texts.push({
        at: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
        text: `Total ${fmtPt(pathLength(pts, false))} m`,
        angleRad: f.angle,
        nx: f.nx,
        ny: f.ny,
        offsetMm: DIM_TOTAL_GAP_MM,
      })
    }
  }
  return out
}

/** Largura de um texto em Helvetica, em mm de papel, para o PDF poder
 *  CENTRAR (o PDF não tem `text-anchor`: quem escreve tem de saber a
 *  largura). Tabela das larguras reais das base-14 para os caracteres
 *  que aparecem numa cota; o resto cai no 0,556 em, que é a largura dos
 *  algarismos. Aproximação assumida, e sempre a mesma nos testes. */
export function helveticaWidthMm(text: string, sizeMm: number): number {
  let em = 0
  for (const ch of String(text ?? '')) {
    em += HELVETICA_EM[ch] ?? 0.556
  }
  return em * sizeMm
}

const HELVETICA_EM: Record<string, number> = {
  ' ': 0.278,
  ',': 0.278,
  '.': 0.278,
  '-': 0.333,
  '=': 0.584,
  '²': 0.4,
  A: 0.667,
  P: 0.667,
  T: 0.611,
  a: 0.556,
  l: 0.222,
  m: 0.833,
  o: 0.556,
  t: 0.278,
}

/** Filtra uma lista vinda de JSON (vista gravada de outra versão,
 *  localStorage mexido à mão). Devolve undefined quando não sobra nada,
 *  para o campo poder ser simplesmente omitido. */
export function sanitizeDimensions(raw: unknown): Dimension2D[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const out: Dimension2D[] = []
  const seen = new Set<string>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const o = item as Record<string, unknown>
    if (typeof o.id !== 'string' || o.id === '' || seen.has(o.id)) continue
    const kind =
      o.kind === 'area' ? 'area' : o.kind === 'chain' ? 'chain' : 'linear'
    const d = makeDimension(cleanDimensionPoints(o.points), kind, o.id, {
      sizeMm: typeof o.sizeMm === 'number' ? o.sizeMm : undefined,
      total: o.total === false ? false : undefined,
      rings: Array.isArray(o.rings) ? (o.rings as Point2[][]) : undefined,
      area: typeof o.area === 'number' ? o.area : undefined,
      perimeter: typeof o.perimeter === 'number' ? o.perimeter : undefined,
    })
    if (!d) continue
    seen.add(o.id)
    out.push(d)
  }
  return out.length > 0 ? out : undefined
}
